import jQuery from 'jquery';
import scriptjs from 'scriptjs';
import Forms from './Forms';
import debounce from 'lodash/debounce';
import AsyncView from './helpers/shopify-asyncview';
import morphdom_esm from 'morphdom';
import { AddressForm } from '@shopify/theme-addresses';
import { rimg_shopify } from './rimg';
import { FormatMoney } from '@components/Shared/Utils/price';
export default class StaticCart {
    constructor(section) {
        this.settings = section.data.settings;
        this.shipping = section.data.shipping;
        this.updateTimeout = null;
        this.$window = jQuery(window);
        this.$el = jQuery(section.el);
        this.el = section.el;
        this.$total = this.$el.find('[data-cart-total]');
        this.$shipping = this.$el.find('[data-cartshipping]'); // Product form containers
        this.$titleTotalSmall = this.$el.find('.cart-title-total--small');
        this.$titleTotalLarge = this.$el.find('.cart-title-total--large');
        this.$titleTotalContents = this.$el.find('[data-cart-title-total]'); // Cart list
        this.cartItemList = this.$el[0].querySelector('[data-cart-item-list]');
        this.cartDiscounts = this.$el[0].querySelector('[data-cart-discounts]'); // Shipping calculator elements
        this.$shippingToggle = this.$el.find('[data-cartshipping-toggle]');
        this.$shippingResponse = this.$shipping.find('[data-cartshipping-response]');
        this.$shippingResponseMessage = this.$shippingResponse.find('[data-cartshipping-message]');
        this.$shippingResponseRates = this.$shippingResponse.find('[data-cartshipping-rates]');
        this.$shippingSubmit = this.$shipping.find('[data-cartshipping-submit]');
        this._moveTitleTotal();
        const $scripts = jQuery('[data-scripts]');
        this._quantityKeyUp = this._quantityKeyUp.bind(this);
        this._editItemQuantity = this._editItemQuantity.bind(this);
        scriptjs($scripts.data('shopify-api-url'), () => {
            this._bindEvents();
            globalThis.Shopify.onError = this._handleErrors.bind(this);
        });
        this.forms = new Forms(this.$el);
        if (this.settings.shipping && this.$shipping.length) {
            scriptjs($scripts.data('shopify-countries'), () => {
                scriptjs($scripts.data('shopify-common'), () => {
                    this._initShippingCalc();
                });
            });
        }
    }
    onSectionUnload() {
        this.$el.off('.cart-page');
        this.$window.off('.cart-page');
        this.forms.unload();
    }
    _bindEvents() {
        this.$el.on('keyup.cart-page', '[data-cartitem-quantity]', this._quantityKeyUp);
        this.$el.on('keydown.cart-page', '[data-cartitem-quantity]', this._quantityKeyDown);
        /*
         * There are two [data-cartitem-quantity] selectors.
         * Use the input as the source of truth rather than the select
         * because the input has an infinite value range whereas the select
         * has a finite range.
         */
        this.$el.on('change.cart-page', '[data-cartitem-quantity][data-quantity-input]', this._editItemQuantity);
        this.$el.on('click.cart-page', '[data-cartitem-remove]', (event) => {
            event.preventDefault();
            this._editItemQuantity(event, true);
        });
        this.$window.on('resize.cart-page', debounce(() => this._moveTitleTotal(), 20));
    }
    /**
     * Gets the current value of the quantity input box for a given line item key
     *
     * @param {string} key
     */
    _getItemQuantity(key) {
        return parseInt(this.el.querySelector(`[data-cartitem-key="${key}"] [data-quantity-input]`).value, 10);
    }
    _moveTitleTotal() {
        if (!this.$titleTotalContents.length) {
            return;
        }
        if (this.$window.outerWidth() >= 480) {
            if (!jQuery.contains(this.$titleTotalLarge[0], this.$titleTotalContents[0])) {
                const $form = this.$titleTotalContents.detach();
                this.$titleTotalLarge.append($form);
            }
        }
        else if (!jQuery.contains(this.$titleTotalSmall[0], this.$titleTotalContents[0])) {
            const $form = this.$titleTotalContents.detach();
            this.$titleTotalSmall.append($form);
        }
    }
    /**
     * Update form depending on key input
     *
     * @param event
     * @private
     */
    _quantityKeyUp(event) {
        if (event.key === 'Backspace') {
            // We block update on backspace so UI doesn't revert to select dropdown
            // when user is trying to edit quantities with the text input.
            if (this.updateTimeout !== null) {
                clearTimeout(this.updateTimeout);
                this.updateTimeout = null;
            }
            return;
        }
        this._editItemQuantity(event);
    }
    /**
     * Prevent form submission when pressing `enter` within a quantity selector
     *
     * @param event
     * @private
     */
    _quantityKeyDown(event) {
        if (event.key === 'Enter') {
            event.preventDefault();
        }
    }
    /**
     * Handle an item quantity change
     *
     * @param event
     * @param {Boolean} remove - Set as true to remove cart item
     * @private
     */
    _editItemQuantity(event, remove = false) {
        var _a, _b;
        const $target = jQuery(event.currentTarget);
        const cartItemRow = $target.closest('[data-cartitem-id]')[0];
        if (remove) {
            cartItemRow.classList.add('removing');
        }
        const quantity = remove
            ? 0
            : parseInt((_b = (_a = cartItemRow.querySelector('.form-field.visible [data-cartitem-quantity]')) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : '0', 10);
        const key = cartItemRow.getAttribute('data-cartitem-key');
        this._updateCart(key, quantity);
    }
    /**
     * Update cart with a valid quantity
     *
     * @param $cartItem
     * @param quantity
     * @private
     */
    _updateCart(key, quantity) {
        // cancel any pending requests
        if (this.updateTimeout !== null) {
            clearTimeout(this.updateTimeout);
        }
        this.updateTimeout = setTimeout(() => {
            if (quantity > 0 && this._getItemQuantity(key) !== quantity) {
                this.updateTimeout = null;
                return;
            }
            const thisTimeoutId = this.updateTimeout; // Notify Shopify updated item
            globalThis.Shopify.changeItem(key, quantity, (response) => {
                if (this.updateTimeout !== thisTimeoutId) {
                    return;
                }
                this._didUpdate(response, thisTimeoutId);
            });
        }, 300);
    }
    /**
     * Fetches new cart contents and swaps into page
     *
     * @param response
     * @param {integer} thisTimeoutId Id of timeout for this request. If no longer current, update is cancelled.
     * @returns {*}
     * @private
     */
    _didUpdate(response, thisTimeoutId) {
        // Reload page if all items are removed from cart
        if (!response.items.length) {
            globalThis.location.href = globalThis.Theme.routes.cart_url;
            return;
        } // Reload the cart-item-list and the discounts snippets
        AsyncView
            .load(globalThis.Theme.routes.cart_url, {
            view: '_ajax',
        })
            .then(({ html }) => {
            // If another request is in progress, discard this update
            if (this.updateTimeout !== thisTimeoutId) {
                return;
            }
            const countEvent = new CustomEvent('cartcount:update', {
                detail: response,
            });
            globalThis.dispatchEvent(countEvent);
            this.$total.each((i, el) => {
                el.innerHTML = FormatMoney(
                // eslint-disable-line no-param-reassign
                response.total_price, this.settings.money_format);
            }); // Inject new cart list contents
            const newListContainer = document.createElement('div');
            newListContainer.innerHTML = html.list;
            morphdom_esm(this.cartItemList, newListContainer.querySelector('ul'), {
                onBeforeElUpdated: (fromEl, toEl) => {
                    // Skip images if src matches
                    // - we don't want to reload lazy loaded images
                    if (fromEl.tagName === 'IMG' && fromEl.src === toEl.src) {
                        return false;
                    }
                    return true;
                },
            });
            rimg_shopify.watch(this.cartItemList);
            this.forms.unload();
            this.forms = new Forms(this.$el);
            this.$el.off('keyup.cart-page', '[data-cartitem-quantity]');
            this.$el.on('keyup.cart-page', '[data-cartitem-quantity]', this._quantityKeyUp);
            this.$el.off('keydown.cart-page', '[data-cartitem-quantity]');
            this.$el.on('keydown.cart-page', '[data-cartitem-quantity]', this._quantityKeyDown);
            this.$el.off('change.cart-page', '[data-cartitem-quantity][data-quantity-input]');
            this.$el.on('change.cart-page', '[data-cartitem-quantity][data-quantity-input]', this._editItemQuantity);
            this.$el.off('click.cart-page', '[data-cartitem-remove]');
            this.$el.on('click.cart-page', '[data-cartitem-remove]', (event) => {
                event.preventDefault();
                this._editItemQuantity(event, true);
            }); // Inject new cart level discounts
            this.cartDiscounts.innerHTML = html.discounts;
        })
            .catch(() => globalThis.location.reload());
    }
    /**
     * Handle Errors returned from Shopify
     *
     * @param errors
     * @private
     */
    _handleErrors(errors = null) {
        if (!errors) {
            return;
        }
        const shippingResponse = {
            message: this.shipping.error_general,
        };
        if (errors.zip && errors.zip.length > 0) {
            if (errors.zip[0].indexOf('is not valid') !== -1 || errors.zip[0].indexOf("can't be blank") !== -1) {
                shippingResponse.message = `${this.shipping.zip} ${errors.zip}`;
            }
        }
        if (errors.error && errors.error.length > 0) {
            if (errors.error[0].indexOf('shipment_too_heavy') !== -1) {
                shippingResponse.message = this.shipping.shipment_too_heavy;
            }
        }
        this._handleShippingResponse(shippingResponse);
    }
    _initShippingCalc() {
        this._bindShippingCalcEvents();
        const countrySelect = document.getElementById('address_country');
        const provinceSelect = document.getElementById('address_province');
        const provinceContainer = document.getElementById('address_province_container');
        this.shippingCountryProvinceSelector = AddressForm(countrySelect, provinceSelect, {
            onCountryChange: (provinces) => {
                if (provinces.length) {
                    provinceContainer.style.display = 'block';
                }
                else {
                    provinceContainer.style.display = 'none';
                } // "Province", "State", "Region", etc. and "Postal Code", "ZIP Code", etc.
                // Even countries without provinces include a label.
                const { label, zip_label: zipLabel } = globalThis.Countries[countrySelect.value];
                provinceContainer.querySelector('label[for="address_province"]').innerHTML = label;
                this.el.querySelector('#address_zip ~ label[for="address_zip"]').innerHTML = zipLabel;
            },
        });
    }
    _bindShippingCalcEvents() {
        this.$el.on('click.cart-page', '[data-cartshipping-toggle]', () => {
            this._toggleShippingCalc();
        });
        this.$el.on('click.cart-page', '[data-cartshipping-submit]', () => {
            this._getShippingRates();
        });
        this.$el.on('keypress.cart-page', '#address_zip', (event) => {
            if (event.keyCode === 10 || event.keyCode === 13) {
                event.preventDefault();
                this.$shippingSubmit.trigger('click');
            }
        });
    }
    _toggleShippingCalc() {
        const oldText = this.$shippingToggle.text();
        const newText = this.$shippingToggle.data('cartshipping-toggle');
        this.$shippingToggle.text(newText).data('cartshipping-toggle', oldText);
        this.$shipping.toggleClass('open');
    }
    _getShippingRates() {
        this._disableShippingButton();
        const shippingAddress = {};
        shippingAddress.country = jQuery('#address_country').val() || '';
        shippingAddress.province = jQuery('#address_province').val() || '';
        shippingAddress.zip = jQuery('#address_zip').val() || '';
        const queryString = Object.keys(shippingAddress)
            .map((key) => `${encodeURIComponent(`shipping_address[${key}]`)}=${encodeURIComponent(shippingAddress[key])}`)
            .join('&');
        jQuery
            .ajax(`${globalThis.Theme.routes.cart_url}/shipping_rates.json?${queryString}`, {
            dataType: 'json',
        })
            .fail((error) => this._handleErrors(error.responseJSON || {}))
            .done((response) => {
            const rates = response.shipping_rates;
            const addressBase = [];
            if (shippingAddress.zip.length) {
                addressBase.push(shippingAddress.zip.trim());
            }
            if (shippingAddress.province.length) {
                addressBase.push(shippingAddress.province);
            }
            if (shippingAddress.country.length) {
                addressBase.push(shippingAddress.country);
            }
            const address = addressBase.join(', ');
            let message = '';
            if (rates.length > 1) {
                const firstRate = FormatMoney(rates[0].price, this.settings.money_format);
                message = this.shipping.multiple_rates
                    .replace('*address*', address)
                    .replace('*number_of_rates*', rates.length)
                    .replace('*rate*', `<span class="money">${firstRate}</span>`);
            }
            else if (rates.length === 1) {
                message = this.shipping.one_rate.replace('*address*', address);
            }
            else {
                message = this.shipping.no_rates;
            }
            const ratesList = rates.map((rate) => {
                const price = FormatMoney(rate.price, this.settings.money_format);
                const rateValue = this.shipping.rate_value.replace('*rate_title*', rate.name).replace('*rate*', `<span class="money">${price}</span>`);
                return `<li>${rateValue}</li>`;
            });
            this._handleShippingResponse({
                message,
                rates: ratesList,
            });
        });
    }
    _enableShippingButton() {
        this.$shippingSubmit.text(this.shipping.calculate_shipping).attr('disabled', false);
    }
    _disableShippingButton() {
        this.$shippingSubmit.text(this.shipping.calculating).attr('disabled', true);
    }
    _showShippingResponse() {
        this.$shippingResponse.addClass('visible');
    }
    _hideShippingResponse() {
        this.$shippingResponse.removeClass('visible');
    }
    /**
     * Handle shipping responses
     *
     * @param {object} shippingResponse
     * @property {String} shippingResponse.messages - Error / Success message
     * @property {Array|String} shippingResponse.rates - Shipping rates
     * @private
     */
    _handleShippingResponse(shippingResponse = {}) {
        // Hide the response so that it can be populated smoothly
        this._hideShippingResponse();
        const message = shippingResponse.message || null;
        const rates = shippingResponse.rates || null; // Empty out contents
        this.$shippingResponseMessage.empty();
        this.$shippingResponseRates.empty();
        if (message) {
            this.$shippingResponseMessage.html(message);
        }
        if (rates) {
            this.$shippingResponseRates.html(rates);
        } // Reset the calculating button so it can be used again
        this._enableShippingButton(); // No error provided
        if (!message && !rates) {
            return;
        } // Show the response
        this._showShippingResponse();
    }
}
